import { Finding } from '../services/policy-gap-analysis.service';

export const findingsMockData: Finding[] = [
  // === Document 1: Information Security Policy v3.2.pdf (28 findings) ===

  // Access Control
  {
    id: 1, document_id: 1, status: 'validated', domain: 'access_control',
    title: 'Multi-factor authentication for administrative accounts',
    original_text: 'All administrative accounts must use multi-factor authentication for access to critical systems.',
    normalized_text: 'MFA is enforced for all administrative accounts accessing critical systems.',
    explanation: 'Entra ID conditional access policies confirm MFA is enforced for all accounts with administrative roles. 100% compliance observed across 47 admin accounts.',
    evidence_source: 'Entra ID', confidence: 'high', severity: 'critical', source_reference: 'Section 4.2.1', is_aggregate: false,
  },
  {
    id: 2, document_id: 1, status: 'validated', domain: 'access_control',
    title: 'Role-based access control implementation',
    original_text: 'Access to systems and data shall be granted based on the principle of least privilege using role-based access control.',
    normalized_text: 'RBAC with least privilege is implemented for all system and data access.',
    explanation: 'Entra ID role assignments show structured RBAC implementation. 94% of users have appropriate role-based assignments with no excessive privileges detected.',
    evidence_source: 'Entra ID', confidence: 'high', severity: 'high', source_reference: 'Section 4.2.3', is_aggregate: false,
  },
  {
    id: 3, document_id: 1, status: 'documented_not_validated', domain: 'access_control',
    title: 'Quarterly access reviews',
    original_text: 'Access rights shall be reviewed quarterly to ensure appropriateness and compliance with the principle of least privilege.',
    normalized_text: 'Access rights are reviewed on a quarterly basis.',
    explanation: 'No evidence of quarterly access review processes found in Entra ID audit logs. Last access review detected was 7 months ago.',
    evidence_source: 'Entra ID', confidence: 'high', severity: 'high', source_reference: 'Section 4.2.5', is_aggregate: false,
  },
  {
    id: 4, document_id: 1, status: 'recommended_not_documented', domain: 'access_control',
    title: 'Privileged access workstations',
    original_text: null,
    normalized_text: 'Dedicated privileged access workstations (PAWs) should be used for administrative tasks.',
    explanation: 'Industry best practice recommends using dedicated privileged access workstations for administrative tasks to reduce attack surface. No mention of PAWs found in documentation.',
    evidence_source: null, confidence: 'medium', severity: 'medium', source_reference: null, is_aggregate: false,
  },

  // Endpoint Security
  {
    id: 5, document_id: 1, status: 'validated', domain: 'endpoint_security',
    title: 'EDR deployed to all managed endpoints',
    original_text: 'Endpoint detection and response solutions shall be deployed on all company-managed endpoints.',
    normalized_text: 'EDR is deployed to all managed endpoints.',
    explanation: 'SentinelOne EDR agent is confirmed active on 98.7% of managed endpoints (1,247 of 1,263). 16 endpoints pending deployment are newly provisioned devices.',
    evidence_source: 'EDR', confidence: 'high', severity: 'critical', source_reference: 'Section 5.1.2', is_aggregate: false,
  },
  {
    id: 6, document_id: 1, status: 'validated', domain: 'endpoint_security',
    title: 'Endpoint protection automatic updates',
    original_text: 'Endpoint protection signatures and engines must be updated automatically.',
    normalized_text: 'Endpoint protection solutions receive automatic signature and engine updates.',
    explanation: 'EDR telemetry confirms automatic updates are enabled. 99.1% of agents are running the latest signature version within 24 hours of release.',
    evidence_source: 'EDR', confidence: 'high', severity: 'high', source_reference: 'Section 5.1.4', is_aggregate: false,
  },
  {
    id: 7, document_id: 1, status: 'documented_not_validated', domain: 'endpoint_security',
    title: 'USB device restrictions',
    original_text: 'Removable media usage shall be restricted and monitored on all endpoints.',
    normalized_text: 'USB and removable media usage is restricted and monitored on endpoints.',
    explanation: 'EDR device control policies show USB restrictions are configured but enforcement is set to "audit only" mode rather than "block" on 62% of endpoints.',
    evidence_source: 'EDR', confidence: 'high', severity: 'medium', source_reference: 'Section 5.3.1', is_aggregate: false,
  },
  {
    id: 8, document_id: 1, status: 'potentially_verifiable', domain: 'endpoint_security',
    title: 'Full disk encryption on all endpoints',
    original_text: 'All company endpoints must have full disk encryption enabled.',
    normalized_text: 'Full disk encryption is enabled on all company endpoints.',
    explanation: 'EDR does not currently report disk encryption status. This could be verified by integrating with Microsoft Intune or a dedicated encryption management solution.',
    evidence_source: null, confidence: 'low', severity: 'high', source_reference: 'Section 5.2.1', is_aggregate: false,
  },

  // Identity & Authentication
  {
    id: 9, document_id: 1, status: 'validated', domain: 'identity_authentication',
    title: 'Password complexity requirements',
    original_text: 'Passwords must be at least 14 characters and include uppercase, lowercase, numbers, and special characters.',
    normalized_text: 'Password policy enforces minimum 14 characters with complexity requirements.',
    explanation: 'Entra ID password policy confirms minimum 14-character requirement with complexity enforcement enabled. Policy applies to all user accounts.',
    evidence_source: 'Entra ID', confidence: 'high', severity: 'high', source_reference: 'Section 4.3.2', is_aggregate: false,
  },
  {
    id: 10, document_id: 1, status: 'documented_not_validated', domain: 'identity_authentication',
    title: 'Service account rotation',
    original_text: 'Service account credentials shall be rotated every 90 days.',
    normalized_text: 'Service account credentials are rotated on a 90-day cycle.',
    explanation: 'Entra ID audit logs show 4 of 12 service accounts have credentials older than 180 days. Automated rotation does not appear to be configured.',
    evidence_source: 'Entra ID', confidence: 'high', severity: 'critical', source_reference: 'Section 4.3.5', is_aggregate: false,
  },

  // Vulnerability Management
  {
    id: 11, document_id: 1, status: 'documented_not_validated', domain: 'vulnerability_management',
    title: 'Weekly vulnerability scanning',
    original_text: 'Vulnerability scans shall be performed on all systems at least weekly.',
    normalized_text: 'Vulnerability scans are performed weekly across all systems.',
    explanation: 'Vulnerability scan data shows scans are performed on average every 18 days, not weekly as documented. Last 90-day scan frequency is approximately bi-weekly.',
    evidence_source: 'Vulnerability Data', confidence: 'high', severity: 'high', source_reference: 'Section 6.1.1', is_aggregate: false,
  },
  {
    id: 12, document_id: 1, status: 'validated', domain: 'vulnerability_management',
    title: 'Critical vulnerability remediation SLA',
    original_text: 'Critical vulnerabilities must be remediated within 72 hours of discovery.',
    normalized_text: 'Critical vulnerabilities are remediated within 72 hours.',
    explanation: 'Vulnerability data shows average remediation time for critical findings is 48 hours over the past 90 days. 91% of critical vulnerabilities met the 72-hour SLA.',
    evidence_source: 'Vulnerability Data', confidence: 'high', severity: 'critical', source_reference: 'Section 6.1.3', is_aggregate: false,
  },
  {
    id: 13, document_id: 1, status: 'recommended_not_documented', domain: 'vulnerability_management',
    title: 'Vulnerability risk acceptance process',
    original_text: null,
    normalized_text: 'A formal risk acceptance process should exist for vulnerabilities that cannot be remediated.',
    explanation: 'Best practice recommends a documented risk acceptance workflow with approval authority and expiration dates for vulnerabilities that cannot be immediately remediated.',
    evidence_source: null, confidence: 'medium', severity: 'medium', source_reference: null, is_aggregate: false,
  },

  // Incident Response
  {
    id: 14, document_id: 1, status: 'not_verifiable', domain: 'incident_response',
    title: 'Annual incident response plan testing',
    original_text: 'The incident response plan shall be tested at least annually through tabletop exercises.',
    normalized_text: 'Incident response plan is tested annually via tabletop exercises.',
    explanation: 'CyFlare does not currently have visibility into tabletop exercise execution. This is an organizational process that cannot be verified through technical telemetry alone.',
    evidence_source: null, confidence: 'low', severity: 'medium', source_reference: 'Section 7.1.4', is_aggregate: false,
  },
  {
    id: 15, document_id: 1, status: 'validated', domain: 'incident_response',
    title: 'Security incident detection and alerting',
    original_text: 'Security incidents must be detected and escalated within 15 minutes during business hours.',
    normalized_text: 'Security incidents are detected and escalated within 15 minutes during business hours.',
    explanation: 'XDR detection and response metrics show average detection-to-escalation time of 8.3 minutes during business hours over the past 30 days.',
    evidence_source: 'XDR', confidence: 'high', severity: 'critical', source_reference: 'Section 7.2.1', is_aggregate: false,
  },

  // Logging & Monitoring
  {
    id: 16, document_id: 1, status: 'validated', domain: 'logging_monitoring',
    title: '24/7 security monitoring',
    original_text: 'Security events shall be monitored 24/7/365 by qualified security personnel.',
    normalized_text: '24/7/365 security monitoring is provided by qualified personnel.',
    explanation: 'XDR platform data confirms continuous monitoring with CyFlare SOC coverage. Alert response times are consistent across all hours with no coverage gaps detected.',
    evidence_source: 'XDR', confidence: 'high', severity: 'critical', source_reference: 'Section 8.1.1', is_aggregate: false,
  },
  {
    id: 17, document_id: 1, status: 'documented_not_validated', domain: 'logging_monitoring',
    title: '90-day log retention',
    original_text: 'Security logs shall be retained for a minimum of 90 days online and 1 year in archive.',
    normalized_text: 'Security logs are retained for 90 days online and 1 year in archive.',
    explanation: 'XDR platform shows 60-day online retention configured, not the 90 days documented. Archive retention policy could not be verified.',
    evidence_source: 'XDR', confidence: 'high', severity: 'medium', source_reference: 'Section 8.2.3', is_aggregate: false,
  },
  {
    id: 18, document_id: 1, status: 'recommended_not_documented', domain: 'logging_monitoring',
    title: 'Log integrity monitoring',
    original_text: null,
    normalized_text: 'Log integrity verification mechanisms should be implemented to detect tampering.',
    explanation: 'Best practice recommends implementing log integrity monitoring (e.g., hash verification, write-once storage) to detect unauthorized log modification.',
    evidence_source: null, confidence: 'medium', severity: 'medium', source_reference: null, is_aggregate: false,
  },

  // Security Awareness
  {
    id: 19, document_id: 1, status: 'not_verifiable', domain: 'security_awareness',
    title: 'Annual security awareness training',
    original_text: 'All employees shall complete security awareness training annually.',
    normalized_text: 'Security awareness training is completed by all employees annually.',
    explanation: 'CyFlare does not have integration with training platforms. Training completion cannot be verified through available telemetry.',
    evidence_source: null, confidence: 'low', severity: 'medium', source_reference: 'Section 9.1.1', is_aggregate: false,
  },
  {
    id: 20, document_id: 1, status: 'recommended_not_documented', domain: 'security_awareness',
    title: 'Phishing simulation program',
    original_text: null,
    normalized_text: 'Regular phishing simulation exercises should be conducted to measure and improve employee awareness.',
    explanation: 'Best practice recommends conducting regular phishing simulations (at least quarterly) to assess and improve employee security awareness.',
    evidence_source: null, confidence: 'medium', severity: 'medium', source_reference: null, is_aggregate: false,
  },

  // Backup & Recovery
  {
    id: 21, document_id: 1, status: 'not_verifiable', domain: 'backup_recovery',
    title: 'Daily backup execution',
    original_text: 'Critical systems and data shall be backed up daily.',
    normalized_text: 'Daily backups are performed for all critical systems and data.',
    explanation: 'Backup infrastructure is not currently monitored through CyFlare integrations. Backup execution cannot be verified through available evidence sources.',
    evidence_source: null, confidence: 'low', severity: 'high', source_reference: 'Section 10.1.1', is_aggregate: false,
  },
  {
    id: 22, document_id: 1, status: 'potentially_verifiable', domain: 'backup_recovery',
    title: 'Quarterly backup restoration testing',
    original_text: 'Backup restoration shall be tested quarterly to ensure data recoverability.',
    normalized_text: 'Backup restoration is tested quarterly.',
    explanation: 'Backup restoration testing could potentially be verified by integrating with backup management platforms such as Veeam, Commvault, or Azure Backup.',
    evidence_source: null, confidence: 'low', severity: 'high', source_reference: 'Section 10.2.1', is_aggregate: false,
  },
  {
    id: 23, document_id: 1, status: 'recommended_not_documented', domain: 'backup_recovery',
    title: 'Immutable backup copies',
    original_text: null,
    normalized_text: 'Immutable backup copies should be maintained to protect against ransomware and unauthorized deletion.',
    explanation: 'Best practice recommends maintaining immutable backup copies that cannot be modified or deleted, providing protection against ransomware attacks.',
    evidence_source: null, confidence: 'medium', severity: 'high', source_reference: null, is_aggregate: false,
  },

  // Remaining findings for doc 1 to reach 28
  {
    id: 24, document_id: 1, status: 'validated', domain: 'identity_authentication',
    title: 'Conditional access policies',
    original_text: 'Access to cloud resources shall be governed by conditional access policies based on user risk, device compliance, and location.',
    normalized_text: 'Conditional access policies govern cloud resource access based on risk signals.',
    explanation: 'Entra ID shows 12 active conditional access policies covering user risk, device compliance, and location-based controls.',
    evidence_source: 'Entra ID', confidence: 'high', severity: 'high', source_reference: 'Section 4.4.1', is_aggregate: false,
  },
  {
    id: 25, document_id: 1, status: 'documented_not_validated', domain: 'vulnerability_management',
    title: 'Patch management within 30 days',
    original_text: 'Non-critical security patches shall be applied within 30 days of release.',
    normalized_text: 'Non-critical patches are applied within 30 days.',
    explanation: 'Vulnerability data shows average patching time for non-critical findings is 45 days. Only 67% of non-critical patches meet the 30-day window.',
    evidence_source: 'Vulnerability Data', confidence: 'high', severity: 'medium', source_reference: 'Section 6.2.1', is_aggregate: false,
  },
  {
    id: 26, document_id: 1, status: 'documented_not_validated', domain: 'logging_monitoring',
    title: 'Network traffic analysis',
    original_text: 'Network traffic shall be analyzed for anomalous behavior patterns.',
    normalized_text: 'Network traffic is analyzed for anomalous behavior.',
    explanation: 'XDR detection rules include some network-based detections, but comprehensive network traffic analysis (NTA/NDR) is not currently deployed.',
    evidence_source: 'XDR', confidence: 'medium', severity: 'medium', source_reference: 'Section 8.3.2', is_aggregate: false,
  },
  {
    id: 27, document_id: 1, status: 'validated', domain: 'incident_response',
    title: 'Automated threat containment',
    original_text: 'The SOC shall have the ability to automatically contain identified threats on endpoints.',
    normalized_text: 'Automated threat containment capabilities exist for endpoint threats.',
    explanation: 'XDR and EDR integration confirms automated containment actions are configured. 23 automated containment actions executed in the past 30 days.',
    evidence_source: 'XDR', confidence: 'high', severity: 'critical', source_reference: 'Section 7.3.1', is_aggregate: false,
  },
  {
    id: 28, document_id: 1, status: 'potentially_verifiable', domain: 'identity_authentication',
    title: 'Third-party access management',
    original_text: 'Third-party vendor access shall be governed by time-limited credentials with just-in-time provisioning.',
    normalized_text: 'Third-party access uses time-limited credentials with JIT provisioning.',
    explanation: 'Partial visibility through Entra ID guest accounts, but full JIT provisioning verification would require integration with a privileged access management (PAM) solution.',
    evidence_source: 'Entra ID', confidence: 'low', severity: 'high', source_reference: 'Section 4.5.2', is_aggregate: false,
  },

  // === Document 2: Access Control Procedures.docx (12 findings) ===
  {
    id: 29, document_id: 2, status: 'validated', domain: 'access_control',
    title: 'Onboarding access provisioning workflow',
    original_text: 'New employee access shall be provisioned through an approved workflow with manager authorization.',
    normalized_text: 'Access provisioning for new employees follows an approved workflow with manager authorization.',
    explanation: 'Entra ID access packages and approval workflows are configured. 100% of new accounts in the past 90 days were provisioned through the approval workflow.',
    evidence_source: 'Entra ID', confidence: 'high', severity: 'high', source_reference: 'Section 2.1', is_aggregate: false,
  },
  {
    id: 30, document_id: 2, status: 'validated', domain: 'access_control',
    title: 'Offboarding account deprovisioning',
    original_text: 'User accounts shall be disabled within 24 hours of employment termination.',
    normalized_text: 'User accounts are disabled within 24 hours of termination.',
    explanation: 'Entra ID audit logs show average account disablement time of 4.2 hours after termination notification. 100% compliance with 24-hour SLA over past 6 months.',
    evidence_source: 'Entra ID', confidence: 'high', severity: 'critical', source_reference: 'Section 2.3', is_aggregate: false,
  },
  {
    id: 31, document_id: 2, status: 'validated', domain: 'access_control',
    title: 'Emergency access procedures',
    original_text: 'Emergency access accounts (break-glass) shall be maintained and monitored.',
    normalized_text: 'Break-glass emergency access accounts exist and are monitored.',
    explanation: 'Two break-glass accounts identified in Entra ID with monitoring alerts configured. Last legitimate use was 45 days ago during a planned maintenance event.',
    evidence_source: 'Entra ID', confidence: 'high', severity: 'critical', source_reference: 'Section 2.5', is_aggregate: false,
  },
  {
    id: 32, document_id: 2, status: 'validated', domain: 'identity_authentication',
    title: 'Single sign-on for SaaS applications',
    original_text: 'All approved SaaS applications shall be integrated with the corporate identity provider for single sign-on.',
    normalized_text: 'SSO is configured for all approved SaaS applications through corporate IdP.',
    explanation: 'Entra ID enterprise applications show 34 SaaS applications configured with SSO. Shadow IT analysis from XDR indicates 3 unapproved applications in use without SSO.',
    evidence_source: 'Entra ID', confidence: 'medium', severity: 'high', source_reference: 'Section 3.2', is_aggregate: false,
  },
  {
    id: 33, document_id: 2, status: 'documented_not_validated', domain: 'access_control',
    title: 'Segregation of duties enforcement',
    original_text: 'Critical functions shall enforce segregation of duties to prevent any single individual from having end-to-end control.',
    normalized_text: 'Segregation of duties is enforced for critical functions.',
    explanation: 'Entra ID role analysis shows 3 accounts with both security administrator and global administrator roles, potentially violating segregation of duties principles.',
    evidence_source: 'Entra ID', confidence: 'medium', severity: 'high', source_reference: 'Section 2.7', is_aggregate: false,
  },
  {
    id: 34, document_id: 2, status: 'validated', domain: 'access_control',
    title: 'Session timeout configuration',
    original_text: 'Interactive sessions shall timeout after 15 minutes of inactivity.',
    normalized_text: 'Interactive sessions have a 15-minute inactivity timeout.',
    explanation: 'Entra ID conditional access policies confirm session timeout set to 15 minutes for all interactive sessions across configured applications.',
    evidence_source: 'Entra ID', confidence: 'high', severity: 'medium', source_reference: 'Section 3.4', is_aggregate: false,
  },
  {
    id: 35, document_id: 2, status: 'validated', domain: 'identity_authentication',
    title: 'Account lockout policy',
    original_text: 'Accounts shall be locked after 5 consecutive failed login attempts.',
    normalized_text: 'Account lockout activates after 5 failed login attempts.',
    explanation: 'Entra ID smart lockout configuration confirms lockout threshold of 5 attempts with 60-second lockout duration, progressively increasing.',
    evidence_source: 'Entra ID', confidence: 'high', severity: 'high', source_reference: 'Section 3.5', is_aggregate: false,
  },
  {
    id: 36, document_id: 2, status: 'documented_not_validated', domain: 'access_control',
    title: 'Privileged account inventory review',
    original_text: 'A complete inventory of privileged accounts shall be maintained and reviewed monthly.',
    normalized_text: 'Privileged account inventory is maintained and reviewed monthly.',
    explanation: 'While Entra ID PIM provides an account inventory, no evidence of monthly review cadence was detected. Last Privileged Identity Management review was 4 months ago.',
    evidence_source: 'Entra ID', confidence: 'high', severity: 'high', source_reference: 'Section 2.9', is_aggregate: false,
  },
  {
    id: 37, document_id: 2, status: 'recommended_not_documented', domain: 'access_control',
    title: 'Just-in-time privileged access',
    original_text: null,
    normalized_text: 'Just-in-time (JIT) privileged access should be implemented to minimize standing privilege exposure.',
    explanation: 'Entra ID PIM supports JIT access but is not currently configured for most administrative roles. Best practice recommends eliminating standing admin access.',
    evidence_source: 'Entra ID', confidence: 'medium', severity: 'high', source_reference: null, is_aggregate: false,
  },
  {
    id: 38, document_id: 2, status: 'not_verifiable', domain: 'access_control',
    title: 'Physical access control alignment',
    original_text: 'Physical access controls shall align with logical access permissions.',
    normalized_text: 'Physical access controls are aligned with logical access permissions.',
    explanation: 'Physical access control systems are not integrated with CyFlare. Alignment between physical and logical access cannot be verified through available data sources.',
    evidence_source: null, confidence: 'low', severity: 'medium', source_reference: 'Section 4.1', is_aggregate: false,
  },
  {
    id: 39, document_id: 2, status: 'potentially_verifiable', domain: 'identity_authentication',
    title: 'Certificate-based authentication for devices',
    original_text: 'Corporate devices shall use certificate-based authentication where supported.',
    normalized_text: 'Certificate-based authentication is used for corporate device authentication.',
    explanation: 'Partial visibility in Entra ID sign-in logs. Full verification would require integration with a certificate authority or MDM solution for certificate lifecycle tracking.',
    evidence_source: 'Entra ID', confidence: 'low', severity: 'medium', source_reference: 'Section 3.8', is_aggregate: false,
  },
  {
    id: 40, document_id: 2, status: 'validated', domain: 'access_control',
    title: 'Guest account governance',
    original_text: 'External guest accounts shall be reviewed quarterly and removed when no longer needed.',
    normalized_text: 'Guest accounts are reviewed quarterly and removed when no longer required.',
    explanation: 'Entra ID access reviews show quarterly guest account review configured and last executed 6 weeks ago. 12 guest accounts were removed in the most recent review.',
    evidence_source: 'Entra ID', confidence: 'high', severity: 'medium', source_reference: 'Section 2.11', is_aggregate: false,
  },

  // === Document 3: Incident Response Plan 2024.pdf (10 findings) ===
  {
    id: 41, document_id: 3, status: 'validated', domain: 'incident_response',
    title: 'Incident classification framework',
    original_text: 'Security incidents shall be classified using a four-tier severity model (Critical, High, Medium, Low).',
    normalized_text: 'Incidents are classified using a four-tier severity model.',
    explanation: 'XDR alert and case data confirms four-tier severity classification in active use. All 156 cases in the past 90 days were properly classified.',
    evidence_source: 'XDR', confidence: 'high', severity: 'medium', source_reference: 'Section 3.1', is_aggregate: false,
  },
  {
    id: 42, document_id: 3, status: 'documented_not_validated', domain: 'incident_response',
    title: 'Incident communication SLA',
    original_text: 'Customer notification for critical incidents shall occur within 1 hour of confirmed detection.',
    normalized_text: 'Customer notification for critical incidents occurs within 1 hour.',
    explanation: 'XDR case data shows average notification time for critical incidents is 1.8 hours. Only 55% of critical incidents met the 1-hour notification SLA in the past 90 days.',
    evidence_source: 'XDR', confidence: 'high', severity: 'critical', source_reference: 'Section 4.2', is_aggregate: false,
  },
  {
    id: 43, document_id: 3, status: 'documented_not_validated', domain: 'incident_response',
    title: 'Post-incident review within 5 days',
    original_text: 'A post-incident review shall be conducted within 5 business days of incident closure.',
    normalized_text: 'Post-incident reviews are completed within 5 business days of closure.',
    explanation: 'XDR case data shows post-incident reviews are documented for only 40% of resolved critical and high severity incidents. Average completion time when conducted is 8 business days.',
    evidence_source: 'XDR', confidence: 'high', severity: 'high', source_reference: 'Section 6.1', is_aggregate: false,
  },
  {
    id: 44, document_id: 3, status: 'validated', domain: 'logging_monitoring',
    title: 'Centralized log collection',
    original_text: 'All security-relevant logs shall be forwarded to the centralized SIEM/XDR platform.',
    normalized_text: 'Security logs are forwarded to centralized SIEM/XDR platform.',
    explanation: 'XDR data source inventory confirms log collection from 14 distinct sources including endpoints, firewalls, cloud services, and identity systems.',
    evidence_source: 'XDR', confidence: 'high', severity: 'high', source_reference: 'Section 2.3', is_aggregate: false,
  },
  {
    id: 45, document_id: 3, status: 'documented_not_validated', domain: 'incident_response',
    title: 'Incident evidence preservation',
    original_text: 'Digital evidence shall be preserved following chain-of-custody procedures for all critical incidents.',
    normalized_text: 'Digital evidence is preserved with chain-of-custody for critical incidents.',
    explanation: 'No structured evidence preservation or chain-of-custody process detected in XDR case management. Forensic data collection appears ad-hoc.',
    evidence_source: 'XDR', confidence: 'medium', severity: 'high', source_reference: 'Section 5.2', is_aggregate: false,
  },
  {
    id: 46, document_id: 3, status: 'recommended_not_documented', domain: 'incident_response',
    title: 'Automated incident playbooks',
    original_text: null,
    normalized_text: 'Automated incident response playbooks should be implemented for common incident types.',
    explanation: 'Best practice recommends automated response playbooks for common scenarios (phishing, malware, unauthorized access) to reduce response time and ensure consistency.',
    evidence_source: null, confidence: 'medium', severity: 'medium', source_reference: null, is_aggregate: false,
  },
  {
    id: 47, document_id: 3, status: 'documented_not_validated', domain: 'logging_monitoring',
    title: 'Alert tuning review cadence',
    original_text: 'Detection rules and alert thresholds shall be reviewed and tuned monthly.',
    normalized_text: 'Detection rules and alerts are reviewed and tuned monthly.',
    explanation: 'XDR rule change audit shows detection rule updates occur approximately every 6-8 weeks rather than monthly. 12 rule modifications in the past 90 days.',
    evidence_source: 'XDR', confidence: 'medium', severity: 'medium', source_reference: 'Section 2.5', is_aggregate: false,
  },
  {
    id: 48, document_id: 3, status: 'recommended_not_documented', domain: 'incident_response',
    title: 'Threat intelligence integration',
    original_text: null,
    normalized_text: 'Threat intelligence feeds should be integrated into detection and response workflows.',
    explanation: 'Best practice recommends integrating threat intelligence feeds to improve detection accuracy and provide context during incident investigation.',
    evidence_source: null, confidence: 'medium', severity: 'medium', source_reference: null, is_aggregate: false,
  },
  {
    id: 49, document_id: 3, status: 'not_verifiable', domain: 'incident_response',
    title: 'External communication plan',
    original_text: 'An external communication plan shall be maintained for incidents requiring regulatory notification.',
    normalized_text: 'External communication plan exists for regulatory incident notification.',
    explanation: 'External communication planning is an organizational process. CyFlare cannot verify the existence or completeness of regulatory notification procedures through technical telemetry.',
    evidence_source: null, confidence: 'low', severity: 'high', source_reference: 'Section 4.5', is_aggregate: false,
  },
  {
    id: 50, document_id: 3, status: 'potentially_verifiable', domain: 'logging_monitoring',
    title: 'SOAR platform integration',
    original_text: 'The incident response process shall leverage security orchestration and automated response capabilities.',
    normalized_text: 'SOAR capabilities are integrated into the incident response process.',
    explanation: 'Partial SOAR capabilities exist through XDR automated actions. Full verification would require integration visibility into the complete SOAR platform configuration.',
    evidence_source: 'XDR', confidence: 'low', severity: 'medium', source_reference: 'Section 2.7', is_aggregate: false,
  },

  // === Document 4: Vulnerability Management Standard.docx (14 findings) ===
  {
    id: 51, document_id: 4, status: 'validated', domain: 'vulnerability_management',
    title: 'Authenticated vulnerability scanning',
    original_text: 'Vulnerability scans shall use authenticated scanning for comprehensive assessment.',
    normalized_text: 'Authenticated scanning is used for vulnerability assessments.',
    explanation: 'Vulnerability scan configuration data confirms authenticated scanning is enabled for 89% of scan targets. 11% of targets use unauthenticated scanning due to agent limitations.',
    evidence_source: 'Vulnerability Data', confidence: 'high', severity: 'high', source_reference: 'Section 2.2', is_aggregate: false,
  },
  {
    id: 52, document_id: 4, status: 'validated', domain: 'vulnerability_management',
    title: 'Vulnerability severity classification',
    original_text: 'Vulnerabilities shall be classified using CVSS scoring aligned with organizational risk context.',
    normalized_text: 'CVSS scoring with organizational risk context is used for vulnerability classification.',
    explanation: 'Vulnerability data confirms CVSS-based severity classification. Risk scoring includes environmental modifiers for asset criticality.',
    evidence_source: 'Vulnerability Data', confidence: 'high', severity: 'medium', source_reference: 'Section 3.1', is_aggregate: false,
  },
  {
    id: 53, document_id: 4, status: 'validated', domain: 'vulnerability_management',
    title: 'High vulnerability remediation within 30 days',
    original_text: 'High severity vulnerabilities shall be remediated within 30 days.',
    normalized_text: 'High severity vulnerabilities are remediated within 30 days.',
    explanation: 'Vulnerability data shows 78% of high severity findings are remediated within 30 days. Average remediation time is 22 days for high severity.',
    evidence_source: 'Vulnerability Data', confidence: 'high', severity: 'high', source_reference: 'Section 4.2', is_aggregate: false,
  },
  {
    id: 54, document_id: 4, status: 'documented_not_validated', domain: 'vulnerability_management',
    title: 'External penetration testing annually',
    original_text: 'External penetration testing shall be conducted annually by a qualified third party.',
    normalized_text: 'Annual external penetration testing is conducted by a qualified third party.',
    explanation: 'No penetration test results or evidence of third-party testing engagements found in available data. This is typically managed outside of automated security tooling.',
    evidence_source: null, confidence: 'medium', severity: 'high', source_reference: 'Section 5.1', is_aggregate: false,
  },
  {
    id: 55, document_id: 4, status: 'validated', domain: 'endpoint_security',
    title: 'Endpoint vulnerability agent coverage',
    original_text: 'Vulnerability assessment agents shall be deployed on all managed endpoints.',
    normalized_text: 'Vulnerability assessment agents are deployed on all managed endpoints.',
    explanation: 'Vulnerability scan data shows agent-based scanning covers 95% of managed endpoints (1,200 of 1,263). Gap is consistent with EDR deployment gap for newly provisioned devices.',
    evidence_source: 'Vulnerability Data', confidence: 'high', severity: 'high', source_reference: 'Section 2.4', is_aggregate: false,
  },
  {
    id: 56, document_id: 4, status: 'documented_not_validated', domain: 'vulnerability_management',
    title: 'Vulnerability trending and reporting',
    original_text: 'Monthly vulnerability trend reports shall be generated and reviewed by security leadership.',
    normalized_text: 'Monthly vulnerability trend reports are generated and reviewed by leadership.',
    explanation: 'Vulnerability platform has reporting capabilities, but no evidence of monthly report generation or leadership review cadence was found.',
    evidence_source: 'Vulnerability Data', confidence: 'medium', severity: 'medium', source_reference: 'Section 6.1', is_aggregate: false,
  },
  {
    id: 57, document_id: 4, status: 'validated', domain: 'vulnerability_management',
    title: 'Asset discovery scanning',
    original_text: 'Automated asset discovery scans shall be performed to identify unmanaged devices.',
    normalized_text: 'Automated asset discovery identifies unmanaged devices.',
    explanation: 'Vulnerability platform performs network-based asset discovery. 15 previously unmanaged devices were identified and onboarded in the past 90 days.',
    evidence_source: 'Vulnerability Data', confidence: 'high', severity: 'high', source_reference: 'Section 2.6', is_aggregate: false,
  },
  {
    id: 58, document_id: 4, status: 'recommended_not_documented', domain: 'vulnerability_management',
    title: 'Container and cloud workload scanning',
    original_text: null,
    normalized_text: 'Vulnerability scanning should be extended to container images and cloud workloads.',
    explanation: 'Best practice recommends scanning container images and cloud workloads for vulnerabilities, especially as organizations increase cloud adoption.',
    evidence_source: null, confidence: 'medium', severity: 'medium', source_reference: null, is_aggregate: false,
  },
  {
    id: 59, document_id: 4, status: 'documented_not_validated', domain: 'endpoint_security',
    title: 'Application whitelisting',
    original_text: 'Application whitelisting shall be enforced on critical servers and workstations.',
    normalized_text: 'Application whitelisting is enforced on critical servers and workstations.',
    explanation: 'EDR data shows application control policies are configured on only 23% of critical servers. No application whitelisting detected on workstations.',
    evidence_source: 'EDR', confidence: 'high', severity: 'high', source_reference: 'Section 7.2', is_aggregate: false,
  },
  {
    id: 60, document_id: 4, status: 'recommended_not_documented', domain: 'vulnerability_management',
    title: 'Vulnerability disclosure program',
    original_text: null,
    normalized_text: 'A vulnerability disclosure program should be established for responsible reporting by external researchers.',
    explanation: 'Best practice recommends establishing a vulnerability disclosure policy to enable external security researchers to report findings responsibly.',
    evidence_source: null, confidence: 'low', severity: 'low', source_reference: null, is_aggregate: false,
  },
  {
    id: 61, document_id: 4, status: 'documented_not_validated', domain: 'vulnerability_management',
    title: 'Compensating controls for unpatched systems',
    original_text: 'When vulnerabilities cannot be patched within SLA, compensating controls shall be implemented and documented.',
    normalized_text: 'Compensating controls are implemented for systems that cannot be patched within SLA.',
    explanation: 'No evidence of documented compensating controls found for the 47 overdue high-severity vulnerabilities currently in the vulnerability backlog.',
    evidence_source: 'Vulnerability Data', confidence: 'medium', severity: 'high', source_reference: 'Section 4.5', is_aggregate: false,
  },
  {
    id: 62, document_id: 4, status: 'recommended_not_documented', domain: 'endpoint_security',
    title: 'Endpoint hardening baselines',
    original_text: null,
    normalized_text: 'CIS or equivalent hardening baselines should be applied to all endpoints.',
    explanation: 'Best practice recommends applying industry-standard hardening baselines (such as CIS Benchmarks) to all endpoints to reduce the attack surface.',
    evidence_source: null, confidence: 'medium', severity: 'medium', source_reference: null, is_aggregate: false,
  },
  {
    id: 63, document_id: 4, status: 'not_verifiable', domain: 'vulnerability_management',
    title: 'Change management for patches',
    original_text: 'All patches shall follow the change management process prior to deployment in production.',
    normalized_text: 'Patches follow the change management process before production deployment.',
    explanation: 'Change management processes are not visible through CyFlare integrations. Verification would require access to ITSM/change management platforms.',
    evidence_source: null, confidence: 'low', severity: 'medium', source_reference: 'Section 4.7', is_aggregate: false,
  },
  {
    id: 64, document_id: 4, status: 'potentially_verifiable', domain: 'endpoint_security',
    title: 'Network segmentation for critical assets',
    original_text: 'Critical assets shall be placed in segmented network zones with restricted access.',
    normalized_text: 'Network segmentation isolates critical assets with restricted access controls.',
    explanation: 'Some network segmentation evidence exists in XDR network telemetry. Full verification would require firewall rule analysis and network topology mapping.',
    evidence_source: 'XDR', confidence: 'low', severity: 'high', source_reference: 'Section 7.5', is_aggregate: false,
  },
];
